import { Component, computed, inject, OnDestroy, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Sidebar } from "../../components/sidebar/sidebar";
import { MessengerService } from '../../services/messenger/messenger.service';
import { Friendbar } from "../../components/friendbar/friendbar";
import { Settings } from "../../views/home/settings/settings";
import { filter, map } from 'rxjs';
import { ChatService } from '../../services/chat/chat.service';
import { PortraitService } from '../../services/portraits/portrait.service';
import { IdentityService } from '../../services/identity/identity.service';
import { AdminModal } from '../../components/modals/admin-modal/admin-modal';
import { RefreshModal } from '../../components/modals/refresh-modal/refresh-modal';
import { Pager } from "../../views/home/pager/pager";
import { CreateLobbyModal } from '../../components/modals/create-lobby-modal/create-lobby-modal';
import { PasswordModal } from '../../components/modals/password-modal/password-modal';
import { NameplateModal } from '../../components/modals/nameplate-modal/nameplate-modal';
import { AvatarModal } from '../../components/modals/avatar-modal/avatar-modal';
import { NameColorModal } from '../../components/modals/name-color-modal/name-color-modal';
import { LootOpeningModal } from './store/components/loot-opening-modal/loot-opening-modal';
import { SettingsService } from '../../services/settings/settings.service';

@Component({
  selector: 'app-home',
  imports: [RouterOutlet, Sidebar, Friendbar, Settings, AdminModal, RefreshModal, Pager, CreateLobbyModal, PasswordModal, NameplateModal, AvatarModal, NameColorModal, LootOpeningModal],
  templateUrl: './home.html',
  styleUrl: './home.scss',
})
export class Home implements OnDestroy {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private portraitService = inject(PortraitService);
  public messenger = inject(MessengerService);
  private chatService = inject(ChatService);
  private identityService = inject(IdentityService);
  public settingsService = inject(SettingsService);

  public currentUser = computed(() => this.messenger.currentUser());
  public userNameColor = this.settingsService.userNameColor;
  public userStatus = signal<number>(1);

  public isAdmin = computed(() => {
    const claims = this.identityService.getClaims();
    if (!claims) return false;
    return claims.role === 'admin' || claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] === 'admin';
  });

  public isElectron = signal<boolean>(this.checkElectron());

  private routeTitle = signal('RIVAL FORGE');
  pageTitle = computed(() => {
    const arena = this.chatService.arena();
    if (arena) {
      return arena.parentGuid != null ? 'ROOM' : 'LOBBY';
    }
    return this.routeTitle();
  });
  noPadding = signal(false);
  pendingRequests = this.messenger.friendRequests;
  hasPendingRequests = computed(() => this.pendingRequests().length > 0);
  hasUnreadMessages = this.messenger.hasUnreadMessages;

  constructor() {
    // Preload.
    this.portraitService.getAll();
    this.messenger.connect();

    // Handles title and padding changes.
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map(() => {
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        })
      )
      .subscribe((route) => {
        const title = route.snapshot.data['title'] || 'RIVAL FORGE';
        const noPadding = route.snapshot.data['noPadding'] || false;
        this.routeTitle.set(title);
        this.noPadding.set(noPadding);

        /*
         * Automatically close conversation window on navigation.
         */
        const bootstrap = (window as any).bootstrap;
        if (bootstrap && bootstrap.Offcanvas) {
          const pagerEl = document.getElementById('pagerOffcanvas');
          if (pagerEl) {
            const bsOffcanvas = bootstrap.Offcanvas.getInstance(pagerEl);
            if (bsOffcanvas) {
              bsOffcanvas.hide();
            }
          }
        }
      });
  }

  /*
   * Clean up messenger connection on component destroy.
   */
  ngOnDestroy(): void {
    this.messenger.disconnect();
  }

  /*
   * Detect if app is running under Electron wrapper.
   */
  private checkElectron(): boolean {
    return navigator.userAgent.toLowerCase().includes('electron');
  }

  /*
   * Accepts a pending friend request or lobby invite.
   */
  public acceptRequest(requestToAccept: any) {
    if (requestToAccept.isInvite) {
      this.chatService.join(requestToAccept.arenaGuid, requestToAccept.arenaPassword || '');
      this.router.navigate(['/home/chat', requestToAccept.arenaGuid]);
    } else {
      this.messenger.acceptFriendRequest(requestToAccept);
    }
    this.messenger.friendRequests.update(requests => requests.filter(r => r !== requestToAccept));
  }

  /*
   * Declines a pending friend request or lobby invite.
   */
  public declineRequest(requestToDecline: any) {
    if (!requestToDecline.isInvite) {
      this.messenger.declineFriendRequest(requestToDecline);
    }
    this.messenger.friendRequests.update(requests => requests.filter(r => r !== requestToDecline));
  }

  /*
   * Maps numerical or string status to Bootstrap text color class.
   */
  getStatusColor(status: any) {
    switch (status) {
      case 'online':
      case 1: return 'text-success';
      case 'ingame':
      case 3: return 'text-neon-purple';
      case 'away':
      case 2: return 'text-warning';
      default: return 'text-secondary';
    }
  }

  /*
   * Maps numerical status ID to human-readable status string.
   */
  getStatusText(status: any) {
    switch (status) {
      case 1: return 'online';
      case 2: return 'away';
      case 3: return 'ingame';
      default: return status;
    }
  }

  /*
   * Maps status to Bootstrap background badge color class.
   */
  getStatusBgColor(status: any) {
    switch (status) {
      case 'online':
      case 1: return 'bg-success';
      case 'ingame':
      case 3: return 'bg-primary';
      case 'away':
      case 2: return 'bg-warning';
      default: return 'bg-secondary';
    }
  }
}
